<template>
  <div class="admin-members">
    <!-- 페이지 헤더 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">회원 관리</h1>
        <p class="page-subtitle">등록된 회원 목록을 관리합니다</p>
      </div>
      <div class="header-stats">
        <v-chip color="primary" variant="flat">
          <v-icon start size="small">mdi-account-group</v-icon>
          총 {{ members.length }}명
        </v-chip>
      </div>
    </div>

    <!-- 검색 및 필터 -->
    <v-card class="search-card" elevation="1">
      <v-card-text class="pa-4">
        <div class="search-container">
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              label="회원 검색"
              placeholder="이름, 아이디, 기관명 검색"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              style="max-width: 300px;"
            ></v-text-field>
          <v-spacer></v-spacer>
            <v-btn-toggle
              v-model="authFilter"
              mandatory
              density="compact"
              color="primary"
              class="auth-filter-toggle"
            >
              <v-btn value="all" size="small">
                <v-icon start size="small">mdi-account-group</v-icon>
                전체
              </v-btn>
              <v-btn value="active" size="small">
                <v-icon start size="small">mdi-check-circle</v-icon>
                인증됨
              </v-btn>
              <v-btn value="inactive" size="small">
                <v-icon start size="small">mdi-close-circle</v-icon>
                미인증
              </v-btn>
            </v-btn-toggle>
        </div>
      </v-card-text>
    </v-card>

    <!-- 회원 목록 테이블 -->
    <v-card class="members-table-card" elevation="1">
      <v-data-table
        :headers="headers"
        :items="filteredMembers"
        :loading="loading"
        :items-per-page="-1"
        class="members-table"
        hover
      >
        <!-- 회원 정보 -->
        <template v-slot:item.m_user_name="{ item }">
          <div class="member-info">
            <v-avatar size="36" color="primary" class="mr-3">
              <span class="avatar-text">{{ getInitial(item.m_user_name) }}</span>
            </v-avatar>
            <div>
              <div class="member-name">{{ item.m_user_name }}</div>
              <div class="member-id">{{ item.m_id }}</div>
            </div>
          </div>
        </template>

        <!-- 기관명 -->
        <template v-slot:item.m_sisul_name="{ item }">
          <div class="sisul-info" v-if="editingSisulId !== item.m_id" @click="startEditSisulName(item)">
            <v-icon size="small" color="grey" class="mr-1">mdi-domain</v-icon>
            <span class="sisul-name-text">{{ item.m_sisul_name || '-' }}</span>
            <v-icon size="x-small" color="grey" class="ml-1 edit-icon">mdi-pencil</v-icon>
          </div>
          <div v-else class="sisul-edit-container">
            <v-text-field
              v-model="editingSisulName"
              variant="outlined"
              density="compact"
              hide-details
              autofocus
              class="sisul-edit-input"
              @keyup.enter="saveSisulName(item)"
              @keyup.escape="cancelEditSisulName"
            ></v-text-field>
            <v-btn icon size="small" color="success" variant="text" @click="saveSisulName(item)" :loading="savingSisulName">
              <v-icon size="small">mdi-check</v-icon>
            </v-btn>
            <v-btn icon size="small" color="grey" variant="text" @click="cancelEditSisulName">
              <v-icon size="small">mdi-close</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- 인증 여부 (m_isuse) -->
        <template v-slot:item.m_isuse="{ item }">
          <v-switch
            :model-value="item.m_isuse === 'Y'"
            @update:model-value="(val: boolean) => toggleMemberIsuse(item, val)"
            color="success"
            hide-details
            density="compact"
            :loading="updatingId === item.m_id"
            :disabled="updatingId === item.m_id"
          ></v-switch>
        </template>

        <!-- 가입일 -->
        <template v-slot:item.m_wdate="{ item }">
          {{ formatDate(item.m_wdate) }}
        </template>

        <!-- 최근 로그인 -->
        <template v-slot:item.last_login="{ item }">
          <v-btn
            v-if="lastLoginMap[item.m_id]"
            variant="text"
            size="small"
            color="primary"
            @click="openLoginLogs(item)"
            class="login-log-btn"
          >
            <div class="text-left">
              <div class="login-date">{{ formatDate(lastLoginMap[item.m_id]) }}</div>
              <div class="login-time">{{ formatTime(lastLoginMap[item.m_id]) }}</div>
            </div>
          </v-btn>
          <span v-else class="no-login">-</span>
        </template>

        <!-- 요청 기록 -->
        <template v-slot:item.pending_requests="{ item }">
          <v-btn
            :variant="pendingRequestsMap[item.m_sisul_code] ? 'flat' : 'text'"
            size="small"
            :color="pendingRequestsMap[item.m_sisul_code] ? 'warning' : 'grey'"
            @click="openTaxRequestModal(item)"
            class="request-history-btn"
            :class="{ 'has-pending': pendingRequestsMap[item.m_sisul_code] }"
          >
            <v-icon size="16" :class="{ 'mr-1': pendingRequestsMap[item.m_sisul_code] }">
              {{ pendingRequestsMap[item.m_sisul_code] ? 'mdi-file-document-alert' : 'mdi-file-document-outline' }}
            </v-icon>
            <template v-if="pendingRequestsMap[item.m_sisul_code]">
              {{ pendingRequestsMap[item.m_sisul_code] }}건
            </template>
          </v-btn>
        </template>

        <!-- 기능 버튼 -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            color="primary"
            variant="tonal"
            size="small"
            @click="impersonateUser(item)"
            :loading="impersonatingId === item.m_id"
          >
            <v-icon start size="small">mdi-eye</v-icon>
            센터접속
          </v-btn>
        </template>

        <!-- 로딩 상태 -->
        <template v-slot:loading>
          <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
        </template>

        <!-- 데이터 없음 -->
        <template v-slot:no-data>
          <div class="no-data-message">
            <v-icon size="64" color="grey-lighten-1">mdi-account-off</v-icon>
            <p>등록된 회원이 없습니다</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- 로그인 기록 모달 -->
    <v-dialog v-model="loginLogsDialog" max-width="900px">
      <v-card>
        <v-card-title class="log-dialog-title">
          <v-icon class="mr-2">mdi-history</v-icon>
          {{ selectedMember?.m_user_name }}님의 로그인 기록
          <v-spacer></v-spacer>
          <v-btn icon variant="text" @click="loginLogsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text class="pa-0">
          <v-data-table
            :headers="logHeaders"
            :items="loginLogs"
            :loading="loadingLogs"
            :items-per-page="10"
            class="log-table"
            density="compact"
          >
            <!-- 로그 타입 -->
            <template v-slot:item.log_type="{ item }">
              <v-chip 
                :color="getLogTypeColor(item.log_type)" 
                size="small"
                variant="flat"
              >
                <v-icon start size="small">
                  {{ item.log_type === 'LOGIN' ? 'mdi-login' : 'mdi-logout' }}
                </v-icon>
                {{ getLogTypeText(item.log_type) }}
              </v-chip>
            </template>
            
            <!-- 일시 -->
            <template v-slot:item.log_time="{ item }">
              {{ formatDateTime(item.log_time) }}
            </template>
            
            <!-- IP -->
            <template v-slot:item.log_ip="{ item }">
              <code class="ip-code">{{ item.log_ip || '-' }}</code>
            </template>
            
            <!-- 지역 -->
            <template v-slot:item.log_region="{ item }">
              <span v-if="item.log_region">
                <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
                {{ item.log_region }}
              </span>
              <span v-else>-</span>
            </template>
            
            <!-- User Agent -->
            <template v-slot:item.log_agent="{ item }">
              <v-tooltip :text="item.log_agent" location="top" max-width="400">
                <template v-slot:activator="{ props }">
                  <span v-bind="props" class="agent-text">
                    {{ truncateAgent(item.log_agent) }}
                  </span>
                </template>
              </v-tooltip>
            </template>
            
            <!-- 로딩 -->
            <template v-slot:loading>
              <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
            </template>
            
            <!-- 데이터 없음 -->
            <template v-slot:no-data>
              <div class="no-log-message">
                <v-icon size="48" color="grey-lighten-1">mdi-history</v-icon>
                <p>로그인 기록이 없습니다</p>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 임금신고 요청 처리 모달 -->
    <WageTaxRes
      v-model="taxRequestModalVisible"
      :sisul-code="selectedMemberForRequest?.m_sisul_code || ''"
      :sisul-name="selectedMemberForRequest?.m_sisul_name || ''"
      @processed="handleRequestProcessed"
    />

    <!-- 스낵바 -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      location="top"
    >
      {{ snackbar.text }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">닫기</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import WageTaxRes from '@/components/wage/tax/WageTaxRes.vue';

interface Member {
  m_idx: number;
  m_id: string;
  m_sisul_code: string;
  m_longterm_code: string;
  m_sisul_name: string;
  m_biz_gubun: string;
  m_biz_num: string;
  m_user_name: string;
  m_email: string;
  m_phone1: string;
  m_phone2: string;
  m_phone3: string;
  m_role: string;
  m_status: string;
  m_isuse: string;
  m_wdate: string;
  m_rdate: string;
}

interface LoginLog {
  log_idx: number;
  m_idx: number;
  m_id: string;
  m_user_name: string;
  m_sisul_name: string;
  log_type: 'LOGIN' | 'LOGOUT';
  log_ip: string;
  log_region: string;
  log_agent: string;
  log_time: string;
}

// 상태
const loading = ref(false);
const members = ref<Member[]>([]);
const searchQuery = ref('');
const authFilter = ref<'all' | 'active' | 'inactive'>('active'); // 기본값: 인증된 기관만
const impersonatingId = ref<string | null>(null);
const updatingId = ref<string | null>(null);
const lastLoginMap = ref<Record<string, string>>({});
const loginLogsDialog = ref(false);
const loginLogs = ref<LoginLog[]>([]);
const selectedMember = ref<Member | null>(null);
const loadingLogs = ref(false);
const snackbar = ref({
  show: false,
  text: '',
  color: 'info'
});

// 대기중인 요청 관련
const pendingRequestsMap = ref<Record<string, number>>({});
const taxRequestModalVisible = ref(false);
const selectedMemberForRequest = ref<Member | null>(null);

// 기관명 편집 상태
const editingSisulId = ref<string | null>(null);
const editingSisulName = ref('');
const savingSisulName = ref(false);

// 테이블 헤더
const headers = [
  { title: '회원정보', key: 'm_user_name', sortable: true, width: '200px' },
  { title: '기관명', key: 'm_sisul_name', sortable: true },
  { title: '가입일', key: 'm_wdate', sortable: true, width: '120px' },
  { title: '최근 로그인', key: 'last_login', sortable: false, width: '120px', align: 'center' },
  { title: '요청', key: 'pending_requests', sortable: false, width: '100px', align: 'center' },
  { title: '기능', key: 'actions', sortable: false, width: '100px', align: 'center' },
  { title: '인증', key: 'm_isuse', sortable: true, width: '80px', align: 'center' }
];

// 로그 테이블 헤더
const logHeaders = [
  { title: '구분', key: 'log_type', sortable: false, width: '100px' },
  { title: '일시', key: 'log_time', sortable: true, width: '180px' },
  { title: 'IP', key: 'log_ip', sortable: false, width: '130px' },
  { title: '지역', key: 'log_region', sortable: false, width: '150px' },
  { title: '브라우저', key: 'log_agent', sortable: false }
];

// 필터링된 회원 목록
const filteredMembers = computed(() => {
  let result = members.value;
  
  // 인증 상태 필터
  if (authFilter.value === 'active') {
    result = result.filter(member => member.m_isuse === 'Y');
  } else if (authFilter.value === 'inactive') {
    result = result.filter(member => member.m_isuse !== 'Y');
  }
  
  // 검색어 필터
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(member => 
      member.m_user_name?.toLowerCase().includes(query) ||
      member.m_id?.toLowerCase().includes(query) ||
      member.m_sisul_name?.toLowerCase().includes(query) ||
      `${member.m_phone1 || ''}${member.m_phone2 || ''}${member.m_phone3 || ''}`?.includes(query)
    );
  }
  
  return result;
});

// 회원 목록 로드
const loadMembers = async () => {
  loading.value = true;
  try {
    const response = await axios.get('/api/admin/members', {
      withCredentials: true
    });
    
    if (response.data.success) {
      members.value = response.data.members;
    } else {
      showSnackbar('회원 목록을 불러오는데 실패했습니다.', 'error');
    }
  } catch (error: any) {
    console.error('회원 목록 로드 오류:', error);
    if (error.response?.status === 403) {
      showSnackbar('관리자 권한이 필요합니다.', 'error');
    } else {
      showSnackbar('서버 오류가 발생했습니다.', 'error');
    }
  } finally {
    loading.value = false;
  }
};

// 전체 회원의 최근 로그인 정보 로드
const loadLastLoginInfo = async () => {
  try {
    const response = await axios.get('/api/admin/members/last-login', {
      withCredentials: true
    });
    
    if (response.data.success) {
      lastLoginMap.value = response.data.lastLoginMap;
    }
  } catch (error: any) {
    console.error('최근 로그인 정보 로드 오류:', error);
  }
};

// 대기중인 임금신고 요청 로드
const loadPendingRequests = async () => {
  try {
    const response = await axios.get('/api/wage-process/tax-request/pending-count', {
      withCredentials: true
    });
    
    if (response.data.success) {
      pendingRequestsMap.value = response.data.data || {};
    }
  } catch (error: any) {
    console.error('대기중인 요청 로드 오류:', error);
  }
};

// 임금신고 요청 모달 열기
const openTaxRequestModal = (member: Member) => {
  selectedMemberForRequest.value = member;
  taxRequestModalVisible.value = true;
};

// 요청 처리 완료 시
const handleRequestProcessed = () => {
  loadPendingRequests();
};

// 로그인 기록 모달 열기
const openLoginLogs = async (member: Member) => {
  selectedMember.value = member;
  loginLogsDialog.value = true;
  loadingLogs.value = true;
  
  try {
    const response = await axios.get(`/api/admin/members/${member.m_id}/logs`, {
      withCredentials: true
    });
    
    if (response.data.success) {
      loginLogs.value = response.data.logs;
    } else {
      showSnackbar('로그인 기록을 불러오는데 실패했습니다.', 'error');
    }
  } catch (error: any) {
    console.error('로그인 기록 로드 오류:', error);
    showSnackbar('서버 오류가 발생했습니다.', 'error');
  } finally {
    loadingLogs.value = false;
  }
};

// 기관명 편집 시작
const startEditSisulName = (member: Member) => {
  editingSisulId.value = member.m_id;
  editingSisulName.value = member.m_sisul_name || '';
};

// 기관명 편집 취소
const cancelEditSisulName = () => {
  editingSisulId.value = null;
  editingSisulName.value = '';
};

// 기관명 저장
const saveSisulName = async (member: Member) => {
  if (!editingSisulName.value.trim()) {
    showSnackbar('기관명을 입력해주세요.', 'warning');
    return;
  }
  
  savingSisulName.value = true;
  
  try {
    const response = await axios.put(`/api/admin/members/${member.m_id}/sisul-name`, {
      m_sisul_name: editingSisulName.value.trim()
    }, {
      withCredentials: true
    });
    
    if (response.data.success) {
      // 로컬 상태 업데이트
      const index = members.value.findIndex(m => m.m_id === member.m_id);
      if (index !== -1) {
        members.value[index].m_sisul_name = editingSisulName.value.trim();
      }
      showSnackbar('기관명이 변경되었습니다.', 'success');
      cancelEditSisulName();
    } else {
      showSnackbar(response.data.message || '기관명 변경에 실패했습니다.', 'error');
    }
  } catch (error: any) {
    console.error('기관명 변경 오류:', error);
    showSnackbar(error.response?.data?.message || '서버 오류가 발생했습니다.', 'error');
  } finally {
    savingSisulName.value = false;
  }
};

// 회원 m_isuse 상태 토글
const toggleMemberIsuse = async (member: Member, isActive: boolean) => {
  updatingId.value = member.m_id;
  const newValue = isActive ? 'Y' : 'N';
  
  try {
    const response = await axios.put(`/api/admin/members/${member.m_id}/isuse`, {
      m_isuse: newValue
    }, {
      withCredentials: true
    });
    
    if (response.data.success) {
      // 로컬 상태 업데이트
      const index = members.value.findIndex(m => m.m_id === member.m_id);
      if (index !== -1) {
        members.value[index].m_isuse = newValue;
      }
      showSnackbar(`${member.m_user_name}님의 인증 상태가 변경되었습니다.`, 'success');
    } else {
      showSnackbar(response.data.message || '상태 변경에 실패했습니다.', 'error');
    }
  } catch (error: any) {
    console.error('회원 상태 변경 오류:', error);
    showSnackbar(error.response?.data?.message || '서버 오류가 발생했습니다.', 'error');
  } finally {
    updatingId.value = null;
  }
};

// 대리 로그인 (회원 화면 보기)
const impersonateUser = async (member: Member) => {
  impersonatingId.value = member.m_id;
  
  try {
    // 1. 대리 로그인 토큰 요청
    const response = await axios.post('/api/admin/impersonate', {
      m_id: member.m_id
    }, {
      withCredentials: true
    });
    
    if (response.data.success) {
      const token = response.data.token;
      
      // 2. 새 창에서 토큰을 인증하여 로그인
      // URL에 토큰을 포함하여 새 창 열기
      const newWindow = window.open(
        `/pgm/login?impersonate=${encodeURIComponent(token)}`,
        '_blank',
        'width=1600,height=900'
      );
      
      if (!newWindow) {
        showSnackbar('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.', 'warning');
      } else {
        showSnackbar(`${member.m_user_name}님의 화면을 새 창에서 열었습니다.`, 'success');
      }
    } else {
      showSnackbar(response.data.message || '대리 로그인에 실패했습니다.', 'error');
    }
  } catch (error: any) {
    console.error('대리 로그인 오류:', error);
    showSnackbar(error.response?.data?.message || '서버 오류가 발생했습니다.', 'error');
  } finally {
    impersonatingId.value = null;
  }
};

// 유틸리티 함수들
const getInitial = (name: string) => {
  return name ? name.charAt(0) : '?';
};

const formatBizNum = (num: string) => {
  if (!num) return '-';
  const cleaned = num.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
  }
  return num;
};

const formatPhone = (phone: string) => {
  if (!phone) return '-';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

const formatDate = (date: string) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('ko-KR');
};

const formatTime = (date: string) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
};

const formatDateTime = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.toLocaleDateString('ko-KR')} ${d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
};

const getLogTypeColor = (type: string) => {
  return type === 'LOGIN' ? 'success' : 'warning';
};

const getLogTypeText = (type: string) => {
  return type === 'LOGIN' ? '로그인' : '로그아웃';
};

const truncateAgent = (agent: string) => {
  if (!agent) return '-';
  // 브라우저 정보만 간단히 추출
  if (agent.includes('Chrome')) return 'Chrome';
  if (agent.includes('Firefox')) return 'Firefox';
  if (agent.includes('Safari')) return 'Safari';
  if (agent.includes('Edge')) return 'Edge';
  if (agent.includes('MSIE') || agent.includes('Trident')) return 'IE';
  return agent.length > 30 ? agent.substring(0, 30) + '...' : agent;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active': return 'success';
    case 'inactive': return 'grey';
    case 'pending': return 'warning';
    default: return 'grey';
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case 'active': return '활성';
    case 'inactive': return '비활성';
    case 'pending': return '대기';
    default: return status || '미설정';
  }
};

const showSnackbar = (text: string, color: string = 'info') => {
  snackbar.value = { show: true, text, color };
};

onMounted(() => {
  loadMembers();
  loadLastLoginInfo();
  loadPendingRequests();
});
</script>

<style lang="scss" scoped>
@import '../../assets/styles/fonts.scss';
.search-container {
  display: flex;
  align-items: center;
  gap: 16px;
}
.admin-members {
  font-family: $Noto_Sans_KR;
  padding: 0 16px 16px 16px;
  width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
  border-radius: 12px;
  color: white;
  
  .page-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0;
  }
  
  .page-subtitle {
    font-size: 14px;
    opacity: 0.8;
    margin: 4px 0 0 0;
  }
}

.search-card {
  margin-bottom: 16px;
  border-radius: 12px;
}

.members-table-card {
  border-radius: 12px;
  overflow: hidden;
}

.members-table {
  .member-info {
    display: flex;
    align-items: center;
    
    .avatar-text {
      font-size: 14px;
      font-weight: 600;
      color: white;
    }
    
    .member-name {
      font-weight: 600;
      font-size: 14px;
      color: #333;
    }
    
    .member-id {
      font-size: 12px;
      color: #666;
    }
  }
  
  .sisul-info {
    display: flex;
    align-items: center;
    font-size: 14px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    transition: background 0.2s;
    
    &:hover {
      background: #f5f5f5;
      
      .edit-icon {
        opacity: 1;
      }
    }
    
    .sisul-name-text {
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .edit-icon {
      opacity: 0;
      transition: opacity 0.2s;
    }
  }
  
  .sisul-edit-container {
    display: flex;
    align-items: center;
    gap: 4px;
    
    .sisul-edit-input {
      width: 180px;
      
      :deep(.v-field__input) {
        font-size: 14px;
        padding: 4px 8px;
      }
    }
  }
}

.no-data-message {
  padding: 60px 20px;
  text-align: center;
  color: #999;
  
  p {
    margin-top: 16px;
    font-size: 16px;
  }
}

// 로그인 기록 버튼 스타일
.login-log-btn {
  text-transform: none !important;
  padding: 4px 8px !important;
  min-width: auto !important;
  
  .login-date {
    font-size: 12px;
    font-weight: 500;
  }
  
  .login-time {
    font-size: 11px;
    opacity: 0.8;
  }
}

.no-login {
  color: #999;
  font-size: 13px;
}

.request-history-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  min-width: 36px !important;
  
  &.has-pending {
    animation: pulse-warning 2s infinite;
  }
  
  &:not(.has-pending) {
    opacity: 0.6;
    
    &:hover {
      opacity: 1;
    }
  }
}

@keyframes pulse-warning {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(251, 140, 0, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(251, 140, 0, 0);
  }
}

// 로그 다이얼로그 스타일
.log-dialog-title {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  font-size: 18px;
  font-weight: 600;
  background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
  color: white;
}

.log-table {
  .ip-code {
    background: #f5f5f5;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    color: #555;
  }
  
  .agent-text {
    font-size: 12px;
    color: #666;
    cursor: help;
  }
}

.no-log-message {
  padding: 40px 20px;
  text-align: center;
  color: #999;
  
  p {
    margin-top: 12px;
    font-size: 14px;
  }
}
</style>

